import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { Toast } from '@components/_atoms/Toast';
import { useTranslation } from 'next-i18next';

// components
import Button from '@components/_atoms/Button';
import TextInputBox from '@components/_molecules/TextInputBox';
import DefaultButtonBox from '@components/_molecules/DefaultButtonBox';

// constants
import { ButtonColor, ButtonType, TextInputVariant } from '@constants/atoms';
import { MOBILE_MODE } from '@constants/size';

// hooks
import { usePatchUserCompanyDepartmentFetch } from '@hooks/fetch/useUserFetch';
import ButtonCode from '@constants/log/buttonCode';

const Layout = styled.form``;

const ButtonBoxWrapper = styled.div`
  margin-top: 80px;

  @media ${MOBILE_MODE} {
    margin-top: 246px;
  }
`;

export default function CompanyDepartmentEditForm() {
  const { t } = useTranslation('user');
  const router = useRouter();
  const queryClient = useQueryClient();

  const [companyDepartment, setCompanyDepartment] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const disabled = !companyDepartment || !!errorMessage;

  const { patchUserCompanyDepartmentFetchMutate } = usePatchUserCompanyDepartmentFetch();

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setCompanyDepartment(value);

    checkValidation(value);
  };

  const checkValidation = (value: string) => {
    let message = '';

    if (!value) message = t('edit.require-error-message');
    setErrorMessage(message);
  };

  const handleFormSubmit = () => {
    patchUserCompanyDepartmentFetchMutate(
      { company_department: companyDepartment },
      {
        onSuccess: () => {
          router.back();
          Toast.success(t('edit.success-message'));
          queryClient.invalidateQueries(['user']);
        },
      },
    );
  };

  return (
    <Layout
      onSubmit={(e) => {
        e.preventDefault();
        handleFormSubmit();
      }}>
      <TextInputBox
        title={t('edit.companyDepartment-title')}
        textInput={{
          variant: errorMessage ? TextInputVariant.ERROR : TextInputVariant.DEFAULT,
          value: companyDepartment,
          onChange: handleFormChange,
          placeholder: t('edit.companyDepartment-placeholder'),
        }}
        errorMessage={errorMessage}
        isRequired
      />
      <ButtonBoxWrapper>
        <DefaultButtonBox
          button1={
            <Button
              text={t('edit.back-button')}
              type={ButtonType.Button}
              color={ButtonColor.OutlinedBlack}
              onClick={() => router.back()}
              steelbosoId={ButtonCode.BTN_BACK}
            />
          }
          button2={<Button disabled={disabled} text={t('edit.submit-button')} steelbosoId={ButtonCode.BTN_SUBMIT} />}
        />
      </ButtonBoxWrapper>
    </Layout>
  );
}
